/**
 * CoverClubTeaser — Explore page preview into the dedicated Cover Club
 * experience. Kept intentionally light (a handful of covers + a link)
 * since Cover Club is not part of the primary Explore/Bookshelf/Studio nav.
 */

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { useCoverClub, CoverArt } from "@/components/coverClub";
import { sortByTrending } from "@/components/coverClub/utils/coverHelpers";

export default function CoverClubTeaser() {
  const { entries } = useCoverClub();
  const preview = sortByTrending(entries).slice(0, 5);

  return (
    <section className="u-container mt-14 md:mt-20" data-testid="explore-cover-club-teaser">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="u-eyebrow mb-3"><span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Cover Club</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">What makes a cover catch your eye?</h2>
        </div>
        <Link
          to="/app/cover-club"
          data-testid="explore-cover-club-link"
          className="hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-coral hover:underline sm:inline-flex"
        >
          Explore Cover Club <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-5">
        {preview.map((entry, i) => (
          <motion.div key={entry.bookId} variants={fadeUp} custom={i}>
            <Link to="/app/cover-club" className="block aspect-[2/3] overflow-hidden rounded-xl shadow-soft transition-transform duration-300 hover:-translate-y-1">
              <CoverArt book={entry.book} style={entry.style} className="h-full w-full" />
            </Link>
          </motion.div>
        ))}
      </motion.div>

      <Link to="/app/cover-club" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-coral hover:underline sm:hidden">
        Explore Cover Club <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </section>
  );
}
